import { useAppSelector, useAppTranslation } from "@/utils";
import { OrderSteps } from "@payping/api/app/api/bnplConsumerTypes";
import {
  PhRCertificate,
  PhRInfo,
  PhRMagnifyingGlass,
  PhRNote,
  PhRNotePencil,
  PhRReceipt,
  PhRSignature,
  PhRStamp,
} from "@payping/icons";
import { Steps, Typography } from "@payping/web-components";
import { ComponentProps, FC, useMemo } from "react";

interface StepsProps {
  currentStepError?: boolean;
}
const PayGetOrderLayoutSteps: FC<StepsProps> = ({ currentStepError }) => {
  const { t } = useAppTranslation();
  const order = useAppSelector((state) => state.bnplConsumer.order);
  const publicSteps = (order?.steps ?? []).filter((i) => i.isPublic);
  const currentStep = publicSteps.filter((i) => i.isPassed).length;
  const ItemsTranslates = {
    [OrderSteps.CreditScore]: {
      description: t("pay.getOrder.steps.creditScore.content"),
      icon: <PhRCertificate width={20} />,
    },
    [OrderSteps.FillProfile]: {
      description: t("pay.getOrder.steps.fillProfile.content"),
      icon: <PhRNotePencil width={20} />,
    },
    [OrderSteps.SignPromissory]: {
      description: t("pay.getOrder.steps.signPromissory.content"),
      icon: <PhRSignature width={20} />,
    },
    [OrderSteps.IssuePromissory]: {
      description: t("pay.getOrder.steps.issuePromissory.content"),
      icon: <PhRNote width={20} />,
    },
    [OrderSteps.UploadSignature]: {
      description: t("pay.getOrder.steps.uploadSignature.content"),
      icon: <PhRSignature width={20} />,
    },
    [OrderSteps.UploadVideoAndSignature]: {
      description: t("pay.getOrder.steps.uploadVideoAndSignature.content"),
      icon: <PhRNote width={20} />,
    },

    [OrderSteps.ChequeSubmit]: {
      description: t("pay.getOrder.steps.chequeSubmit.content"),
      icon: <PhRInfo width={20} />,
    },

    [OrderSteps.ChequeInquiry]: {
      description: t("pay.getOrder.steps.chequeInquiry.content"),
      icon: <PhRMagnifyingGlass width={20} />,
    },
    [OrderSteps.SignContract]: {
      description: t("pay.getOrder.steps.signContract.content"),
      icon: <PhRStamp width={20} />,
    },
    [OrderSteps.PayPromissory]: {
      description: t("pay.getOrder.steps.payPromissory.content"),
      icon: <PhRReceipt width={20} />,
    },
    [OrderSteps.Quote]: {
      description: t("pay.getOrder.steps.quote.content"),
      icon: <PhRReceipt width={20} />,
    },
  } as const satisfies { [key in number]: { description: any; icon: any } };

  const items = useMemo(() => {
    const itemsList: ComponentProps<typeof Steps>["items"] = [];
    publicSteps.forEach((item, index) => {
      const isError = currentStepError && currentStep === index;
      const { description } =
        item.code !== undefined && item.code in ItemsTranslates
          ? (ItemsTranslates as any)[item.code as unknown as OrderSteps]
          : { description: "" };

      const icon =
        item.code !== undefined && item.code in ItemsTranslates
          ? (ItemsTranslates as any)[item.code as unknown as OrderSteps]?.icon
          : undefined;

      itemsList.push({
        title: item.name ?? "",
        description:
          order.nextStep === item.code ? <Typography.Text type="secondary">{description}</Typography.Text> : "",
        icon,
        status: isError ? "error" : undefined,
      });
    });
    return itemsList;
  }, [ItemsTranslates, currentStep, currentStepError, order.nextStep, publicSteps]);

  const listLength = items.length * 60;
  return (
    <Steps
      style={{
        height: `${listLength}px`,
      }}
      status="process"
      responsive
      size="small"
      // because first step is 2
      current={currentStep}
      direction="vertical"
      items={items}
    />
  );
};

export default PayGetOrderLayoutSteps;
